import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import ChatList from "@/components/ChatList";
import ChatView from "@/components/ChatView";
import OrderSidebar from "@/components/OrderSidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const selectedConvRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({});
  const phoneParam = new URLSearchParams(window.location.search).get('phone');
  const [filter, setFilter] = useState(phoneParam ? "all" : "expiring");
  const [expiringConversations, setExpiringConversations] = useState([]);
  const filterRef = useRef("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const socketRef = useRef(null);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  }, []);

  // Initialize Socket.IO
  useEffect(() => {
    fetchContacts();
    
    socketRef.current = io(BACKEND_URL, {
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () => {
      console.log("Socket connected");
    });

    socketRef.current.on("new_message", (data) => {
      console.log("New message received:", data);
      
      // Update messages if in current conversation
      if (selectedConvRef.current?.id === data.conversation_id) {
        setMessages((prev) => {
          // Check if message already exists by ID
          if (prev.some(m => m.id === data.message.id)) {
            return prev;
          }
          // Check if this is replacing a temp message
          const tempIdx = prev.findIndex(m => 
            m.id?.startsWith("temp_") && 
            m.content === data.message.content && 
            m.direction === "outgoing"
          );
          if (tempIdx !== -1) {
            const updated = [...prev];
            updated[tempIdx] = data.message;
            return updated;
          }
          // Add new message and sort
          const newMsgs = [...prev, data.message];
          return newMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
      }
      
      // Update conversation in list without full reload
      // Update unread count for new/unread tabs without full refetch
      if (filter === 'new' || filter === 'unread' || filter === 'expiring') {
        setConversations((prev) => {
          const exists = prev.find(c => c.id === data.conversation_id);
          if (!exists) return prev; // Don't add new conversations to filtered view
          return prev; // Keep existing list, just let WebSocket update handle it
        });
      } else {
      setConversations((prev) => {
        const updated = prev.map((conv) => {
          if (conv.id === data.conversation_id) {
            const updateData = {
              ...conv,
              last_message: data.message.content?.substring(0, 50) || "[Media]",
              last_message_time: data.message.timestamp,
            };
            // Update session timer for incoming messages
            if (data.message.direction === "incoming") {
              updateData.last_incoming_message_time = data.message.timestamp;
            }
            return updateData;
          }
          return conv;
        });
        // Sort by last_message_time descending
        return updated.sort((a, b) => 
          new Date(b.last_message_time) - new Date(a.last_message_time)
        );
      });
      }
    });

    socketRef.current.on("conversation_updated", (data) => {
      if (data?.unread_count === 0 && data?.conversation_id) {
        // Another team member replied - reset unread and remove from New/Unread tabs
        setConversations(prev => {
          if (filterRef.current === "new" || filterRef.current === "unread") {
            return prev.filter(c => c.id !== data.conversation_id);
          }
          return prev.map(c => c.id === data.conversation_id ? {...c, unread_count: 0} : c);
        });
      }
    });

    socketRef.current.on("message_status", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.message_id ? { ...msg, status: data.status } : msg
        )
      );
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [selectedConversation]);

  // Fetch initial data
  useEffect(() => {
    fetchConfigs();
    fetchConversations();
    fetchTemplates();
    fetchStats();
  }, []);

  // Fetch conversations when filter/search changes
  useEffect(() => {
    fetchConversations();
    filterRef.current = filter;
  }, [filter, searchQuery, selectedConfig]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/configs`);
      setConfigs(response.data);
      // Default to All Numbers - don't auto-select first config
    } catch (error) {
      console.error("Error fetching configs:", error);
    }
  };

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Pass filters to backend
      if (filter === "unread" || filter === "new") {
        params.append("unread_only", "true");
      } else if (filter === "expiring") {
        // Fetch expiring conversations separately
        const expResp = await axios.get(`${API}/conversations/expiring?hours=4`);
        setExpiringConversations(expResp.data.conversations || []);
        setConversations(expResp.data.conversations || []);
        setLoading(false);
        return;
      } else if (filter === "favourites") {
        params.append("favourites_only", "true");
      } else if (filter !== "all") {
        params.append("status", filter);
      }
      const phoneParam = searchParams.get('phone');
      if (phoneParam) {
        params.append("search", phoneParam);
      } else if (searchQuery) {
        params.append("search", searchQuery);
      }
      if (selectedConfig && !phoneParam) {
        params.append("config_id", selectedConfig);
      }
      
      const response = await axios.get(`${API}/conversations?${params}`);
      let filteredData = response.data;
      
      // Session-based filtering for New and Unread tabs
      if (filter === "new" || filter === "unread") {
        if (response.data.length > 0) {
          try {
            const checkResponse = await axios.post(`${API}/conversations/check-session-replies`, {
              conversation_ids: response.data.map(c => c.id)
            });
            const replyStatus = checkResponse.data;

            if (filter === "new") {
              // New = unread conversations with NO agent reply in 24h session
              filteredData = response.data.filter(c => !replyStatus[c.id]);
            } else {
              // Unread = conversations where customer sent last message (not us)
              filteredData = response.data.filter(c => {
                if (!replyStatus[c.id]) return false;
                // Compare last_message_time vs last_incoming_message_time
                const lastMsg = new Date(c.last_message_time);
                const lastIncoming = new Date(c.last_incoming_message_time);
                // If last incoming is within 2 minutes of last message, customer sent last
                const diffMinutes = (lastMsg - lastIncoming) / 60000;
                return diffMinutes <= 2;
              });
            }
          } catch (err) {
            console.error("Error checking session replies:", err);
          }
        }
      }
      
      // Sort by last_message_time descending (newest first)
      const sortedData = filteredData.sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
      setConversations(sortedData);
      
      // Check if we need to open a specific conversation by phone number
      
      if (phoneParam) {
        // Always use All Numbers when searching by phone
        if (selectedConfig !== null) {
          setSelectedConfig(null);
          return; // Wait for re-fetch with All Numbers
        }
        
        // Search in ALL conversations (response.data), not filtered list
        const targetConv = response.data.find(c => c.phone_number.includes(phoneParam));
        if (targetConv) {
          selectConversation(targetConv);
          toast.success(`Opened conversation: ${targetConv.customer_name || targetConv.phone_number}`);
          setSearchParams({});
        } else if (response.data.length > 0) {
          // Data loaded but not found
          toast.error(`Conversation not found for ${phoneParam}`);
          setSearchParams({});
        }
        // If response.data.length === 0, still loading - wait
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [filter, searchQuery, selectedConfig, searchParams]);

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(
        `${API}/conversations/${conversationId}/messages?limit=500`
      );
      // Sort messages by timestamp chronologically
      const sortedMessages = response.data.sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );
      const deletedIds = JSON.parse(localStorage.getItem("deletedMsgIds") || "[]");
      setMessages(sortedMessages.filter(m => !deletedIds.includes(m.id)));
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const selectConversation = async (conversation) => {
    // Mark PREVIOUS conversation as read when switching (like WhatsApp Web)
    if (selectedConversation && selectedConversation.id !== conversation.id) {
      const prevConv = selectedConversation;
      const prevConvInList = conversations.find(c => c.id === prevConv.id);
      if (prevConvInList && prevConvInList.unread_count > 0) {
        axios.post(`${API}/conversations/${prevConv.id}/mark-read`);
        setConversations((prev) => {
          if (filter === "unread" || filter === "new") {
            return prev.filter((c) => c.id !== prevConv.id);
          }
          return prev.map((c) => c.id === prevConv.id ? {...c, unread_count: 0} : c);
        });
      }
    }

    setSelectedConversation(conversation);
    selectedConvRef.current = conversation;
    await fetchMessages(conversation.id);

    // Join socket room
    if (socketRef.current) {
      socketRef.current.emit("join_conversation", {
        conversation_id: conversation.id,
      });
    }
  };

  const sendMessage = async (content, messageType = "text", replyContext = null) => {
    if (!selectedConversation) return;
    
    // Handle image upload
    if (messageType === "image" && content instanceof File) {
      const file = content;
      const tempMessage = {
        id: "temp_" + Date.now(),
        conversation_id: selectedConversation.id,
        content: "[Uploading image...]",
        message_type: "image",
        direction: "outgoing",
        status: "sending",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempMessage]);

      try {
        // Upload image first
        const formData = new FormData();
        formData.append("file", file);
        
        const uploadResponse = await axios.post(
          `${API}/conversations/${selectedConversation.id}/upload-image`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        
        // Replace temp message with real one
        setMessages((prev) => 
          prev.map((m) => m.id === tempMessage.id ? uploadResponse.data : m)
        );
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to send image");
        setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
      }
      return;
    }
    
    if (!content.trim()) return;

    // Optimistic update - show message immediately
    const tempMessage = {
      id: "temp_" + Date.now(),
      conversation_id: selectedConversation.id,
      content: content,
      message_type: messageType,
      direction: "outgoing",
      status: "sending",
      timestamp: new Date().toISOString(),
      context_message_id: replyContext?.whatsapp_message_id || null,
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.id}/messages`,
        {
          conversation_id: selectedConversation.id,
          message_type: messageType,
          content: content,
          context_message_id: replyContext?.whatsapp_message_id || null,
        }
      );

      // Replace temp message with real one
      setMessages((prev) => 
        prev.map((m) => m.id === tempMessage.id ? response.data : m)
      );
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const updateConversationStatus = async (conversationId, status) => {
    try {
      await axios.put(`${API}/conversations/${conversationId}`, { status });
      toast.success("Status updated");
      setConversations(prev => prev.map(c => c.id === conversationId ? {...c, status} : c));
      fetchStats();
      
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation((prev) => ({ ...prev, status }));
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleToggleFavourite = async (conversationId) => {
    try {
      const response = await axios.put(`${API}/conversations/${conversationId}/toggle-favourite`);
      
      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, is_favourite: response.data.is_favourite }
            : conv
        )
      );
      
      // Update selected conversation if it's the one being toggled
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation((prev) => ({
          ...prev,
          is_favourite: response.data.is_favourite,
        }));
      }
    } catch (error) {
      console.error("Error toggling favourite:", error);
      toast.error("Failed to update favourite status");
    }
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="dashboard">
      {/* Navigation Sidebar */}
      <Sidebar stats={stats} />

      {/* Chat List */}
      <ChatList
        conversations={conversations}
        selectedConversation={selectedConversation}
        onSelectConversation={selectConversation}
        filter={filter}
        onFilterChange={setFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loading={loading}
        configs={configs}
        selectedConfig={selectedConfig}
        onConfigChange={setSelectedConfig}
        contacts={contacts}
        onToggleFavourite={handleToggleFavourite}
        onRefresh={fetchConversations}
      />

      {/* Chat View */}
      <ChatView
        conversation={selectedConversation}
        messages={messages}
        onSendMessage={sendMessage}
        templates={templates}
        onStatusChange={updateConversationStatus}
        onContactAdded={fetchContacts}
        onRefreshMessages={() => selectedConversation && fetchMessages(selectedConversation.id)}
      />

      {/* Order Sidebar - Editable */}
      <OrderSidebar 
        conversation={selectedConversation} 
        onOrderUpdated={fetchConversations}
      />
    </div>
  );
}
